import { faSquarePen } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from 'i18next'
import moment from 'moment'
import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import {
  ROUTES,
  documentTypeId,
  pendingStatusDocument,
  processStatusDocument,
  closeStatusDocument,
  expiredStatusDocument,
  colorsStatus,
  docHeaderStatusId,
  VAPermissions,
} from 'Utils/constants'
import { Formatter } from 'Utils/Formatter'
import { handleCloneQuote, generateColorStatus } from 'Utils/functions'
import useValidatePermission from 'Hooks/useValidatePermission'
import {
  cotizacionUpdate,
  updateDocumentStatus,
} from 'redux/actions/cotizacionActions'
import { useCheckFinalPrice } from 'Components/ActionTable/hooks/useCheckFinalPrice'
import { searchSalesDocumentDetailService } from 'redux/services/cotizacionServices'
import duplicate from 'Assets/Images/BuscarCotizacion/icon-duplicate-blue.svg'
import { CustomSwitch } from 'Components/Switch/CustomSwitch'

export function useColumnSearchQuote() {
  const history = useHistory()
  const dispatch = useDispatch()
  const { checkFinalPrice } = useCheckFinalPrice()
  const isUserAllowedReopenQuote = useValidatePermission(
    VAPermissions.reopenExpiredQuote
  )
  const [reopenedIds, setReopenedIds] = useState(() => new Set())

  const [showModalClone, setshowModalClone] = useState(false)
  const [quoteDataClone, setQuoteDataClone] = useState(null)
  const [isLoadingClone, setIsLoadingClone] = useState(false)
  const [isChangingExpiredStatus, setIsChangingExpiredStatus] = useState(false)
  const [snackbarClone, setSnackbarClone] = useState({
    open: false,
    message: '',
    severity: '',
  })

  const handlePreCloneQuote = async () => {
    setIsLoadingClone(true)
    const requestedAt = Date.now()
    try {
      const response = await searchSalesDocumentDetailService({
        DocumentTypeId: documentTypeId.QUOTATION,
        DocumentHeaderId: quoteDataClone?.DocHeaderId,
        DocumentNumber: quoteDataClone?.DocumentNumber,
        pageNumber: 1,
        pageSize: 1,
      })

      const allData = response?.data?.Data

      if (!allData?.DocHeaderId) {
        throw new Error(
          'No se pudieron obtener los datos completos de la cotización'
        )
      }

      setQuoteDataClone(null)
      dispatch(
        cotizacionUpdate({
          data: allData,
          QuoteHeaderId: quoteDataClone?.DocHeaderId,
          requestedAt,
        })
      )
      await handleCloneQuote({
        lines: allData?.Lines,
        setIsLoading: setIsLoadingClone,
        checkFinalPrice,
        customerId: allData?.CustomerId,
        history,
      })
    } catch (error) {
      setSnackbarClone({
        open: true,
        message: 'Error al clonar la cotización: ' + error.message,
        severity: 'error',
      })
    }
    setIsLoadingClone(false)
  }

  const quoteAllowanceDateExpired = date => {
    if (!date) return false
    const lastModifiedDate = new Date(date)
    const ninetyDaysAgo = new Date()
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90)
    return lastModifiedDate < ninetyDaysAgo
  }

  const handleChangeExpiredStatus = async docHeaderId => {
    setIsChangingExpiredStatus(true)

    const payload = {
      documentId: docHeaderId,
      headerStatusId: docHeaderStatusId.open,
      isQuote: true,
    }

    const response = await dispatch(updateDocumentStatus(payload))

    if (response?.Success) {
      setSnackbarClone({
        open: true,
        message: t('SEARCH_QUOTATION_T.REOPEN_QUOTE_MESSAGES.SUCCESS_MESSAGE'),
        severity: 'success',
      })
      setReopenedIds(prev => new Set([...prev, docHeaderId]))
    } else {
      setSnackbarClone({
        open: true,
        message: t('SEARCH_QUOTATION_T.REOPEN_QUOTE_MESSAGES.ERROR_MESSAGE'),
        severity: 'error',
      })
    }

    setIsChangingExpiredStatus(false)
  }

  const columns = useMemo(
    () => [
      {
        accessor: 'CustomerNameDocument',
        Header: t('SEARCH_QUOTATION_T.CLIENT_NAME'),
        width: '280px',
        Cell: ({ row }) => row.original.CustomerNameDocument || t('UNKNOWN'),
      },
      {
        accessor: 'DocumentNumber',
        Header: t('SEARCH_QUOTATION_T.QUOTATION_NUMBER_LABEL'),
        width: '200px',
      },
      {
        accessor: 'AmountWithTax',
        Header: t('SEARCH_QUOTATION_T.QUOTATION_AMOUNT_LABEL'),
        width: '120px',
        Cell: ({ row }) => {
          const amount = Number.parseFloat(row.original.AmountWithTax)
          return `$ ${Formatter.Money(amount, {
            decimalPrecision: 2,
            rounded: true,
          })}`
        },
      },
      {
        accessor: 'SalesOrderNumber',
        Header: t('SEARCH_QUOTATION_T.SELL_ORDER_NUMBER_LABEL'),
        width: '185px',
        Cell: ({ row }) => row.original.SalesOrderNumber || t('UNKNOWN'),
      },
      {
        accessor: 'CreatedDate',
        Header: t('SEARCH_QUOTATION_T.DATE_QUOTATION_LABEL'),
        width: '110px',
        Cell: ({ row }) =>
          moment(row.original.CreatedDate).format('DD/MM/YYYY'),
      },
      {
        accessor: 'Origin',
        Header: t('SEARCH_QUOTATION_T.ORIGIN_QUOTATION_LABEL'),
        width: '80px',
        Cell: ({ row }) => (row.original.Origin === 'VA' ? 'VA' : 'Web'),
      },
      {
        accessor: 'DocHeaderStatusDescription',
        Header: t('SEARCH_QUOTATION_T.STATUS_LABEL'),
        width: '120px',
        Cell: ({ row }) => {
          const status = row.original.DocHeaderStatusDescription
          const docHeaderId = row.original.DocHeaderId
          const lastModifiedDate = row.original.LastModifiedDate

          const effectiveStatus = reopenedIds.has(docHeaderId)
            ? 'Abierta'
            : status

          const isValidOptionChangeExpiredStatus =
            isUserAllowedReopenQuote &&
            !quoteAllowanceDateExpired(lastModifiedDate)

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span
                style={{
                  backgroundColor: generateColorStatus(
                    effectiveStatus,
                    pendingStatusDocument,
                    processStatusDocument,
                    closeStatusDocument,
                    expiredStatusDocument,
                    colorsStatus
                  ),
                  borderRadius: '5px',
                  padding: '2px 8px',
                  display: 'inline-block',
                }}>
                {effectiveStatus}
              </span>
              {isValidOptionChangeExpiredStatus &&
                effectiveStatus === 'Expirada' && (
                  <CustomSwitch
                    onChange={() => handleChangeExpiredStatus(docHeaderId)}
                  />
                )}
            </div>
          )
        },
      },
      {
        accessor: 'actions',
        Header: t('ACTION_TABLE_T.ACTIONS_LABEL'),
        width: '100px',
        Cell: ({ row }) => {
          const editQuoteUrl = `${ROUTES.editQuoteHeaderId}${row.original?.DocHeaderId}/${row.original?.DocumentNumber}`

          return (
            <span style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Link
                to={editQuoteUrl}
                style={{ display: 'inline-flex', alignItems: 'center' }}
                aria-label="Editar cotización"
                title="Editar cotización"
                data-testid="edit-quote-link">
                <FontAwesomeIcon
                  icon={faSquarePen}
                  style={{
                    color: '#3FC5DF',
                    fontSize: '2rem',
                    cursor: 'pointer',
                  }}
                  data-testid="edit-quote-icon"
                />
              </Link>
              <img
                data-testid="icon-duplicate-quote"
                src={duplicate}
                alt="duplicate"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setshowModalClone(true)
                  setQuoteDataClone(row.original)
                }}
              />
            </span>
          )
        },
      },
    ],
    [history, isUserAllowedReopenQuote, reopenedIds]
  )

  return {
    columns,
    showModalClone,
    setshowModalClone,
    quoteDataClone,
    isLoadingClone,
    setIsLoadingClone,
    isChangingExpiredStatus,
    handlePreCloneQuote,
    checkFinalPrice,
    history,
    snackbarClone,
    setSnackbarClone,
  }
}
